import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatCents, type ReportMonth, type ReportRanking, type ReportsOverview } from '@kontor/shared';
import { api, queryString } from '../../api';
import { Bar, Donut, Legend, type Slice } from '../../components/charts';
import { InfoHint } from '../../components/InfoHint';
import { Modal } from '../../components/Modal';
import { useSession } from '../../session';
import './reports.css';

const MONTH_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const number = (n: number, digits = 1) => n.toLocaleString('de-DE', { maximumFractionDigits: digits });
const percent = (bp: number | null) => (bp === null ? '–' : `${number(bp / 100)} %`);
const hours = (h: number) => `${number(h)} Std.`;

/** Veränderung zum Vorjahr in Prozent, null ohne Vorjahreswert */
function change(now: number, before: number): number | null {
  return before === 0 ? null : ((now - before) / Math.abs(before)) * 100;
}

/** Symbole der Seite: Linien auf einem 24er-Raster, die Farbe kommt aus dem Text */
const ICONS = {
  receipt: 'M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  doc: 'M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6',
  bars: 'M4 20h16M7 16V9M12 16V5M17 16v-4',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  download: 'M12 3v12M8 11l4 4 4-4M4 19h16',
  up: 'M12 19V5M6 11l6-6 6 6',
  down: 'M12 5v14M6 13l6 6 6-6',
  right: 'M5 12h14M13 6l6 6-6 6',
  bank: 'M3 9l9-5 9 5M5 9v9M9 9v9M15 9v9M19 9v9M3 20h18',
  chevron: 'M6 9l6 6 6-6',
  expand: 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7',
} as const;

function Icon({ name, size = 18 }: { name: keyof typeof ICONS; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICONS[name]} />
    </svg>
  );
}

function Delta({ now, before, year, goodWhenUp = true }: { now: number; before: number; year: number; goodWhenUp?: boolean | null }) {
  const pct = change(now, before);
  if (pct === null) return <span className="muted small">{before === 0 && now === 0 ? `Kein Wert ${year}` : `Kein Vergleich mit ${year}`}</span>;
  const up = pct > 0.05;
  const down = pct < -0.05;
  const tone = goodWhenUp === null || (!up && !down) ? '' : up === goodWhenUp ? 'reports-good' : 'reports-bad';
  return (
    <span className={`reports-delta small ${tone}`} title={`${year}: ${formatCents(before)}`}>
      {(up || down) && <Icon name={up ? 'up' : 'down'} size={13} />}
      {up ? '+ ' : ''}
      {number(pct)} % zu {year}
    </span>
  );
}

type Accent = 'blue' | 'red' | 'green';

function Tile({ label, value, hint, icon, accent = 'blue', foot, children, tone }: { label: string; value: string; hint?: string; icon: keyof typeof ICONS; accent?: Accent; foot?: string; children?: ReactNode; tone?: 'good' | 'bad' | 'warn' }) {
  return (
    <div className={`card reports-tile reports-tile--${accent}`}>
      <div className="reports-tile__head">
        <span className="reports-icon"><Icon name={icon} /></span>
        <span className="reports-tile__label">
          {label}
          {hint && <InfoHint label={label}>{hint}</InfoHint>}
        </span>
      </div>
      <strong className={tone ? `reports-${tone}` : ''}>{value}</strong>
      <div className="reports-tile__sub">{children}</div>
      {foot && <span className="reports-tile__foot">{foot}</span>}
    </div>
  );
}

/** Schaltfläche in der Ecke einer Karte, die die ganze Liste in einem Fenster öffnet */
function AllButton({ count, label, onClick }: { count: number; label: string; onClick: () => void }) {
  return (
    <button type="button" className="reports-export reports-all" onClick={onClick} aria-label={`${label}: alle ${count} anzeigen`}>
      <Icon name="expand" size={15} /> Alle {count}
    </button>
  );
}

/** Rangliste mit Balken, der längste Balken ist der Spitzenreiter */
function Ranking({ rows, empty, link }: { rows: ReportRanking[]; empty: string; link?: (row: ReportRanking) => string | null }) {
  if (rows.length === 0) return <p className="muted small">{empty}</p>;
  const max = Math.max(...rows.map((r) => r.cents), 1);
  return (
    <ol className="reports-rank">
      {rows.map((r, i) => {
        const to = link?.(r) ?? null;
        return (
          <li key={`${r.id ?? r.name}-${i}`}>
            <div className="reports-rank__head">
              <span className="reports-rank__n">{i + 1}.</span>
              <span className="reports-rank__name">{to ? <Link to={to}>{r.name}</Link> : r.name}</span>
              <span className="reports-rank__value">{formatCents(r.cents)} <span className="muted small">{number(r.shareBp / 100, 0)} %</span></span>
            </div>
            <div className="reports-bar" aria-hidden="true"><span style={{ width: `${Math.max(2, (r.cents / max) * 100)}%`, animationDelay: `${i * 40}ms` }} /></div>
          </li>
        );
      })}
    </ol>
  );
}

/** Kunden, Produkte und Lieferanten in einer Karte, umschaltbar. So bleibt Platz für das Wichtige. */
function Rankings({ o }: { o: ReportsOverview }) {
  const tabs = [
    o.revenue && { key: 'customers', label: 'Kunden', title: 'Stärkste Kunden', note: 'Nach Umsatz.', rows: o.topCustomers, empty: 'Noch keine Rechnungen in diesem Jahr.', link: (r: ReportRanking) => (r.id ? `/kontakte/${r.id}` : null) },
    o.revenue && { key: 'products', label: 'Produkte', title: 'Umsatzstärkste Produkte', note: 'Nach Umsatz, die besten fünf.', rows: o.topProducts, empty: 'Noch keine Rechnungspositionen in diesem Jahr.', link: (r: ReportRanking) => (r.id ? `/produkte/${r.id}` : null) },
    o.expenses && { key: 'suppliers', label: 'Lieferanten', title: 'Größte Lieferanten', note: 'Nach Einkaufsvolumen.', rows: o.topSuppliers, empty: 'Noch keine freigegebenen Eingangsrechnungen in diesem Jahr.', link: (r: ReportRanking) => (r.id ? `/kontakte/${r.id}` : null) },
  ].filter((t): t is NonNullable<typeof t> => !!t);
  const [key, setKey] = useState(tabs[0]?.key);
  const current = tabs.find((t) => t.key === key) ?? tabs[0];
  if (!current) return null;
  return (
    <section className="card" aria-labelledby="rep-rank">
      <h2 id="rep-rank">{current.title} {o.year}</h2>
      <div className="reports-tabs" role="tablist" aria-label="Rangliste wählen">
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={t.key === current.key} className={t.key === current.key ? 'is-active' : ''} onClick={() => setKey(t.key)}>{t.label}</button>
        ))}
      </div>
      <p className="muted small">{current.note}</p>
      <Ranking key={current.key} rows={current.rows} empty={current.empty} link={current.link} />
    </section>
  );
}

/** Gleichmäßige Achsenwerte in Euro, die den Bereich von `lo` bis `hi` überdecken */
function niceTicks(lo: number, hi: number): number[] {
  const span = hi - lo || 1;
  const raw = span / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const ticks: number[] = [];
  for (let v = Math.floor(lo / step) * step; v <= Math.ceil(hi / step) * step + step / 1e6; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}

type Series = 'revenue' | 'expense' | 'result' | 'previous';
const SERIES_LABEL: Record<Series, string> = { revenue: 'Erlöse', expense: 'Aufwand', result: 'Ergebnis', previous: 'Erlöse Vorjahr' };

/**
 * Verlauf des Jahres: je Monat ein Balken für Erlöse und einer für Aufwand, dazu die Linie des Ergebnisses und auf Wunsch die Erlöse des Vorjahres. Die Einträge oben blenden Reihen
 * ein und aus, die Achse passt sich an. Mit der Maus oder der Tastatur auf einem Monat erscheinen die Werte. Darunter steht dieselbe Zahlenreihe als Tabelle.
 */
function MonthChart({ months, year, previousYear }: { months: ReportMonth[]; year: number; previousYear: number }) {
  const [hidden, setHidden] = useState<Series[]>(['previous']);
  const [hover, setHover] = useState<number | null>(null);
  const show = (s: Series) => !hidden.includes(s);
  const toggle = (s: Series) => setHidden((h) => (h.includes(s) ? h.filter((x) => x !== s) : [...h, s]));
  const results = months.map((m) => (m.revenueCents - m.expenseCents) / 100);
  const previous = months.map((m) => m.previousRevenueCents / 100);
  const values = [...(show('revenue') ? months.map((m) => m.revenueCents / 100) : []), ...(show('expense') ? months.map((m) => m.expenseCents / 100) : []), ...(show('result') ? results : []), ...(show('previous') ? previous : [])];
  const ticks = niceTicks(Math.min(0, ...values), Math.max(1, ...values));
  const lo = ticks[0]!;
  const hi = ticks[ticks.length - 1]!;
  const pos = (euro: number) => ((euro - lo) / (hi - lo)) * 100;
  const zero = pos(0);
  const x = (i: number) => ((i + 0.5) / months.length) * 100;
  const line = (list: number[]) => list.map((r, i) => `${x(i)},${100 - pos(r)}`).join(' ');
  const tip = hover === null ? null : months[hover]!;

  return (
    <>
      <ul className="reports-legend reports-legend--top">
        {(['revenue', 'expense', 'result', 'previous'] as Series[]).map((s) => (
          <li key={s}>
            <button type="button" className="reports-toggle" aria-pressed={show(s)} onClick={() => toggle(s)} title={show(s) ? `${SERIES_LABEL[s]} ausblenden` : `${SERIES_LABEL[s]} einblenden`}>
              <span className={`reports-swatch reports-swatch--${s}`} /> {s === 'previous' ? `Erlöse ${previousYear}` : SERIES_LABEL[s]}
            </button>
          </li>
        ))}
      </ul>
      <div className="reports-plot" onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <div key={t} className="reports-plot__grid" style={{ bottom: `${pos(t)}%` }}>
            <span>{t.toLocaleString('de-DE')} €</span>
          </div>
        ))}
        <div className="reports-plot__cols" role="group" aria-label={`Erlöse, Aufwand und Ergebnis je Monat ${year}`}>
          {months.map((m, i) => (
            <div
              key={m.month}
              className={`reports-plot__col${hover === i ? ' is-hover' : ''}`}
              tabIndex={0}
              aria-label={`${MONTH_SHORT[i]} ${year}: Erlöse ${formatCents(m.revenueCents)}, Aufwand ${formatCents(m.expenseCents)}, Ergebnis ${formatCents(m.revenueCents - m.expenseCents)}, Erlöse ${previousYear} ${formatCents(m.previousRevenueCents)}`}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
            >
              {show('revenue') && <span className="reports-plot__bar reports-plot__bar--revenue" style={{ bottom: `${zero}%`, height: `${Math.max(0, pos(m.revenueCents / 100) - zero)}%`, animationDelay: `${i * 25}ms` }} />}
              {show('expense') && <span className="reports-plot__bar reports-plot__bar--expense" style={{ bottom: `${zero}%`, height: `${Math.max(0, pos(m.expenseCents / 100) - zero)}%`, animationDelay: `${i * 25 + 60}ms` }} />}
            </div>
          ))}
        </div>
        <svg className="reports-plot__line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {show('previous') && <polyline className="reports-plot__line--previous" points={line(previous)} fill="none" vectorEffect="non-scaling-stroke" />}
          {show('result') && <polyline className="reports-plot__line--result" points={line(results)} fill="none" vectorEffect="non-scaling-stroke" />}
        </svg>
        {show('result') && results.map((r, i) => <span key={months[i]!.month} className={`reports-plot__dot${hover === i ? ' is-hover' : ''}`} style={{ left: `${x(i)}%`, bottom: `${pos(r)}%` }} aria-hidden="true" />)}
        {tip && hover !== null && (
          <div className={`reports-tip${hover < 2 ? ' reports-tip--left' : hover > 9 ? ' reports-tip--right' : ''}`} style={{ left: `${x(hover)}%` }} aria-hidden="true">
            <strong>{MONTH_SHORT[hover]} {year}</strong>
            <span><i className="reports-swatch reports-swatch--revenue" /> Erlöse <b>{formatCents(tip.revenueCents)}</b></span>
            <span><i className="reports-swatch reports-swatch--expense" /> Aufwand <b>{formatCents(tip.expenseCents)}</b></span>
            <span><i className="reports-swatch reports-swatch--result" /> Ergebnis <b>{formatCents(tip.revenueCents - tip.expenseCents)}</b></span>
            <span className="muted"><i className="reports-swatch reports-swatch--previous" /> {previousYear} <b>{formatCents(tip.previousRevenueCents)}</b></span>
          </div>
        )}
      </div>
      <div className="reports-plot__labels" aria-hidden="true">{MONTH_SHORT.map((l) => <span key={l}>{l}</span>)}</div>
      <details className="reports-table">
        <summary>Als Tabelle anzeigen</summary>
        <div className="table-card">
          <table className="table table--cards">
            <thead>
              <tr><th>Monat</th><th className="num">Erlöse</th><th className="num">Aufwand</th><th className="num">Ergebnis</th><th className="num">Erlöse {previousYear}</th></tr>
            </thead>
            <tbody>
              {months.map((m, i) => (
                <tr key={m.month}>
                  <td data-label="Monat">{MONTH_SHORT[i]}</td>
                  <td data-label="Erlöse" className="num">{formatCents(m.revenueCents)}</td>
                  <td data-label="Aufwand" className="num">{formatCents(m.expenseCents)}</td>
                  <td data-label="Ergebnis" className="num">{formatCents(m.revenueCents - m.expenseCents)}</td>
                  <td data-label={`Erlöse ${previousYear}`} className="num">{formatCents(m.previousRevenueCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}

/** Schritte von Anfrage bis Auftrag, jeder mit Balken im Verhältnis zum größten Wert der Gruppe */
function Funnel({ steps, color = 1 }: { steps: { label: string; value: number; note?: string }[]; color?: number }) {
  const max = Math.max(...steps.map((s) => s.value), 1);
  return (
    <ul className="reports-steps">
      {steps.map((s, i) => (
        <li key={s.label}>
          <strong>{s.value.toLocaleString('de-DE')}</strong>
          <span>{s.label}</span>
          <div className="reports-steps__bar"><Bar value={s.value} max={max} color={i === 0 ? color : i === steps.length - 1 && steps.length > 2 ? 8 : color} /></div>
          {s.note && <span className="muted small">{s.note}</span>}
        </li>
      ))}
    </ul>
  );
}

type HourRow = { id: string; name: string; hours: number; note?: string };

function Hours({ rows, empty }: { rows: HourRow[]; empty: string }) {
  if (rows.length === 0) return <p className="muted small">{empty}</p>;
  const max = Math.max(...rows.map((r) => r.hours), 1);
  return (
    <ol className="reports-rank reports-rank--plain">
      {rows.map((r, i) => (
        <li key={r.id}>
          <div className="reports-rank__head">
            <span className="reports-rank__name">{r.name}</span>
            <span className="reports-rank__value">{hours(r.hours)}{r.note && <span className="muted small"> · {r.note}</span>}</span>
          </div>
          <div className="reports-bar" aria-hidden="true"><span style={{ width: `${Math.max(2, (r.hours / max) * 100)}%`, animationDelay: `${i * 40}ms` }} /></div>
        </li>
      ))}
    </ol>
  );
}

/** So viele Zeilen zeigt eine Karte, der Rest steht in der ganzen Liste im Fenster */
const SHOWN = 5;

function HoursCard({ id, title, intro, rows, empty, footnote }: { id: string; title: string; intro: ReactNode; rows: HourRow[]; empty: string; footnote?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card" aria-labelledby={id}>
      <div className="reports-card-head">
        <h2 id={id}>{title}</h2>
        {rows.length > SHOWN && <AllButton count={rows.length} label={title} onClick={() => setOpen(true)} />}
      </div>
      <p className="muted small">{intro}</p>
      <Hours rows={rows.slice(0, SHOWN)} empty={empty} />
      {footnote && <p className="muted small">{footnote}</p>}
      <Modal open={open} title={title} onClose={() => setOpen(false)}>
        <Hours rows={rows} empty={empty} />
      </Modal>
    </section>
  );
}

/**
 * Auswertungen (KON-169): Kennzahlen für ein wählbares Jahr mit Vergleich zum Vorjahr. Abschnitte erscheinen nur, wenn der Mandant das zugehörige Modul hat. Die Zahlen sind
 * netto und folgen den Regeln des Finanzreports (Rechnungsdatum, Stornos und Gutschriften mindern). Summen und Konten von Erlösen, Aufwand und Umsatzsteuer stehen im Finanzreport und
 * werden hier nicht wiederholt, dafür der Verlauf über die Monate, offene Posten, Vertrieb, Auslastung und Finanzierungen.
 */
export function ReportsPage() {
  const { can, hasModule } = useSession();
  const [year, setYear] = useState<number | null>(null);
  const query = useQuery({
    queryKey: ['reports-overview', { year }],
    queryFn: async () => (await api<{ data: ReportsOverview }>('GET', `/reports/overview${queryString({ year: year ?? undefined })}`)).data,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const o = query.data;
  const canExport = hasModule('chart') && can('chart.report');

  return (
    <div className="reports-page">
      <div className="reports-head">
        <div className="reports-head__title">
          <h1>Auswertungen</h1>
          <p className="muted">Wichtige Kennzahlen auf einen Blick: klar, aktuell und aussagekräftig.</p>
        </div>
        <div className="reports-head__tools">
          {o && (
            <label className="reports-yearpick">
              <Icon name="calendar" size={16} />
              <select value={o.year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Auswertungsjahr">
                {o.years.map((y) => <option key={y} value={y}>Jahr {y}</option>)}
              </select>
              <Icon name="chevron" size={14} />
            </label>
          )}
          {o && canExport && (
            <a className="reports-export" href={`/api/v1/chart/report${queryString({ from: `${o.year}-01-01`, to: `${o.year}-12-31`, format: 'csv' })}`}>
              <Icon name="download" size={16} /> Exportieren
            </a>
          )}
          {can('chart.report') && <Link to="/finanzreport" className="reports-export">Finanzreport <Icon name="right" size={14} /></Link>}
        </div>
      </div>
      {query.isError && <div className="alert alert--error" role="alert">{(query.error as Error).message}</div>}
      {query.isLoading && <p className="muted">Wird berechnet …</p>}
      {o && <ReportsBody o={o} />}
    </div>
  );
}

function ReportsBody({ o }: { o: ReportsOverview }) {
  const { can } = useSession();
  const { offers, orders, inquiries } = o.sales;
  const shifts = o.utilization.shifts;
  const fleet = o.utilization.fleet;
  const hasMoney = o.revenue !== null || o.expenses !== null;
  const nothing = !hasMoney && !offers && !orders && !inquiries && !shifts && !fleet;
  const [agingActive, setAgingActive] = useState<string | null>(null);
  const agingTotal = o.receivables ? o.receivables.aging.reduce((s, b) => s + b.cents, 0) : 0;
  /** Farben der Altersstufen: neutral für „nicht fällig“, dann von Gelb nach Rot */
  const AGING_COLOR: Record<string, number> = { not_due: 1, d1_30: 3, d31_60: 5, d61_90: 7 };
  const agingSlices: Slice[] = o.receivables ? o.receivables.aging.map((b) => ({ key: b.key, label: b.label, value: b.cents, color: AGING_COLOR[b.key] ?? 4 })) : [];

  return (
    <>
      <p className="muted small reports-note">
        Alle Beträge netto, Vergleich mit {o.previousYear}.
        {hasMoney && can('chart.report') && <> Summen und Konten von Erlösen, Aufwand und Umsatzsteuer stehen im <Link to="/finanzreport">Finanzreport</Link>.</>}
      </p>
      {nothing && <div className="card"><p>Für Ihre Module gibt es noch keine Auswertung.</p></div>}
      {(o.receivables || o.payables || o.revenue || orders) && (
        <div className="reports-tiles">
          {o.receivables && (
            <Tile label="Offene Forderungen" icon="clock" accent="blue" foot="aktuell" value={formatCents(o.receivables.openCents)} tone={o.receivables.overdueCount > 0 ? 'bad' : undefined}>
              <span className="muted small">{o.receivables.openCount} offene Posten{o.receivables.overdueCount > 0 && <>, davon {o.receivables.overdueCount} überfällig ({formatCents(o.receivables.overdueCents)})</>}</span>
            </Tile>
          )}
          {o.payables && (
            <Tile label="Offene Verbindlichkeiten" icon="doc" accent="red" foot="aktuell" value={formatCents(o.payables.openCents)} tone={o.payables.overdueCount > 0 ? 'bad' : undefined}>
              <span className="muted small">{o.payables.openCount} offene Posten{o.payables.overdueCount > 0 && <>, davon {o.payables.overdueCount} überfällig ({formatCents(o.payables.overdueCents)})</>}</span>
            </Tile>
          )}
          {o.revenue && o.revenue.averageInvoiceNetCents !== null && (
            <Tile label="Ø Rechnung" icon="bars" accent="green" foot="pro Jahr" value={formatCents(o.revenue.averageInvoiceNetCents)}>
              <span className="muted small">{o.revenue.invoiceCount} Rechnungen im Jahr</span>
            </Tile>
          )}
          {orders && (
            <Tile label="Noch nicht abgerechnet" icon="receipt" accent="blue" foot="Aufträge, Stand heute" value={formatCents(orders.toInvoiceNetCents)} tone={orders.toInvoiceCount > 0 ? 'warn' : undefined} hint="Erledigte Aufträge, zu denen es noch keine Rechnung gibt. Dieses Geld ist verdient, aber noch nicht in Rechnung gestellt.">
              <span className="muted small">{orders.toInvoiceCount} erledigte Aufträge</span>
            </Tile>
          )}
        </div>
      )}

      <div className="reports-main">
        {hasMoney && (
          <section className="card reports-main__chart" aria-labelledby="rep-months">
            <h2 id="rep-months">Verlauf {o.year}</h2>
            <p className="muted small">Erlöse, Aufwand und Ergebnis je Monat. Die Einträge oben blenden Reihen ein und aus.</p>
            <MonthChart months={o.months} year={o.year} previousYear={o.previousYear} />
          </section>
        )}
        {o.receivables && (
          <section className="card" aria-labelledby="rep-aging">
            <h2 id="rep-aging">Forderungen und Zahlungsverhalten</h2>
            <p className="muted small">Offene Rechnungen nach Altersstruktur.</p>
            <div className="reports-aging-chart">
              <Donut slices={agingSlices} active={agingActive} onActive={setAgingActive} title="Offen" total={formatCents(agingTotal)} format={formatCents} ariaLabel={`Offene Forderungen nach Altersstruktur, zusammen ${formatCents(agingTotal)}`} />
              <Legend slices={agingSlices} active={agingActive} onActive={setAgingActive} format={formatCents} />
            </div>
            <dl className="reports-facts">
              <div><dt>Ø Zahlungsdauer</dt><dd>{o.receivables.averagePaymentDays === null ? '–' : `${number(o.receivables.averagePaymentDays)} Tage`}</dd></div>
              <div><dt>Pünktlich bezahlt</dt><dd>{percent(o.receivables.paidOnTimeBp)}</dd></div>
              <div><dt>Bezahlt {o.year}</dt><dd>{o.receivables.paidCount}</dd></div>
            </dl>
          </section>
        )}
      </div>

      {(inquiries || offers || orders || o.revenue || o.expenses) && (
        <div className="reports-main">
          {(inquiries || offers || orders) && (
            <section className="card reports-main__chart" aria-labelledby="rep-sales">
              <h2 id="rep-sales">Vertrieb {o.year}</h2>
              <p className="muted small">Angelegt im Jahr {o.year}. Die Pipeline zeigt den Stand von heute.</p>
              <div className="reports-columns">
                {inquiries && (
                  <div>
                    <h3>Anfragen</h3>
                    <Funnel color={1} steps={[{ label: 'eingegangen', value: inquiries.received }, { label: 'mit Angebot oder Auftrag', value: inquiries.converted, note: `${percent(inquiries.conversionBp)} Umsetzung` }]} />
                  </div>
                )}
                {offers && (
                  <div>
                    <h3>Angebote</h3>
                    <Funnel
                      color={1}
                      steps={[
                        { label: 'versendet', value: offers.sent },
                        { label: 'angenommen', value: offers.accepted, note: `${formatCents(offers.acceptedNetCents)} netto` },
                        { label: 'abgelehnt oder abgelaufen', value: offers.rejected + offers.expired },
                      ]}
                    />
                    <p className="small">Annahmequote: <strong>{percent(offers.acceptanceBp)}</strong> <span className="muted">der entschiedenen Angebote</span></p>
                    <p className="small">Offen heute: <strong>{offers.openCount}</strong> mit {formatCents(offers.openNetCents)}</p>
                  </div>
                )}
                {orders && (
                  <div>
                    <h3>Aufträge</h3>
                    <Funnel
                      color={2}
                      steps={[
                        { label: 'angelegt', value: orders.created },
                        { label: 'erledigt', value: orders.done, note: `${orders.invoiced} abgerechnet` },
                        { label: 'storniert', value: orders.cancelled },
                      ]}
                    />
                    <p className="small">Ø Auftragswert: <strong>{orders.averageNetCents === null ? '–' : formatCents(orders.averageNetCents)}</strong></p>
                    <p className="small">Ø Dauer bis erledigt: <strong>{orders.averageDaysToComplete === null ? '–' : `${number(orders.averageDaysToComplete)} Tage`}</strong></p>
                    <p className="small">In Arbeit heute: <strong>{orders.openCount}</strong> mit {formatCents(orders.openNetCents)}</p>
                  </div>
                )}
              </div>
            </section>
          )}
          {(o.revenue || o.expenses) && <Rankings o={o} />}
        </div>
      )}

      {o.financing && (
        <section className="card reports-wide" aria-labelledby="rep-financing">
          <div className="reports-section-head">
            <span className="reports-icon reports-icon--lg"><Icon name="bank" size={22} /></span>
            <div>
              <h2 id="rep-financing">Finanzierungen und Leasings</h2>
              <p className="muted small">Aufwand {o.year}: Leasingraten netto, Zinsen und Gebühren. Die Tilgung von Darlehen ist kein Aufwand. Der Betrag ist im Aufwand des Finanzreports schon enthalten.</p>
            </div>
            <Link to="/finanzierungen" className="reports-export reports-section-head__action">Alle anzeigen <Icon name="right" size={14} /></Link>
          </div>
          <div className="reports-stats">
            <div className="reports-stat"><span className="muted small">Aufwand {o.year}</span><strong>{formatCents(o.financing.costNetCents)}</strong><Delta now={o.financing.costNetCents} before={o.financing.previousCostNetCents} year={o.previousYear} goodWhenUp={false} /></div>
            <div className="reports-stat"><span className="muted small">Laufende Verträge</span><strong>{o.financing.activeCount}</strong>{o.financing.overdueCount > 0 && <span className="small reports-bad">{o.financing.overdueCount} Raten überfällig</span>}</div>
            <div className="reports-stat"><span className="muted small">Zahlungen diesen Monat</span><strong>{formatCents(o.financing.monthCents)}</strong><span className="muted small">brutto</span></div>
            <div className="reports-stat"><span className="muted small">Restschuld Darlehen</span><strong>{formatCents(o.financing.loanDebtCents)}</strong><span className="muted small">Darlehen und Ratenkäufe</span></div>
            <div className="reports-stat"><span className="muted small">Leasingverpflichtung</span><strong>{formatCents(o.financing.leaseCommitmentCents)}</strong><span className="muted small">brutto, bis zum Vertragsende</span></div>
          </div>
        </section>
      )}

      {(shifts || fleet) && (
        <div className="reports-pair">
          {shifts && (
            <HoursCard
              id="rep-shifts"
              title={`Arbeitszeit ${o.year}`}
              intro={<>Geplant in veröffentlichten Schichten: <strong>{hours(shifts.totalHours)}</strong></>}
              rows={shifts.employees}
              empty="Noch keine veröffentlichten Schichten in diesem Jahr."
              footnote="Die Mitarbeiter mit den meisten Stunden, abzüglich der Pausen."
            />
          )}
          {fleet && (
            <HoursCard
              id="rep-fleet"
              title={`Fahrzeugeinsatz ${o.year}`}
              intro={<>Eingeplant: <strong>{hours(fleet.totalHours)}</strong></>}
              rows={fleet.vehicles.map((v) => ({ id: v.id, name: v.name, hours: v.hours, note: `${v.daysInUse} Tage` }))}
              empty="Noch keine Einsätze in diesem Jahr."
            />
          )}
        </div>
      )}
    </>
  );
}
